import { Textarea } from '@/components/ui/textarea';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Loader2, Shield, Users, Lock, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { Dialog } from 'primereact/dialog';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth.store';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { PERMISSION_LABELS, getPermissionLabel } from '@/lib/permission-labels';

// Back-compat: the label map moved to @/lib/permission-labels (T-449, one owner).
export { PERMISSION_LABELS };

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function parsePermissions(permissions: unknown): string[] {
  if (typeof permissions === 'string') {
    try {
      return JSON.parse(permissions);
    } catch {
      return [];
    }
  }
  return Array.isArray(permissions) ? (permissions as string[]) : [];
}

function groupPermissions(perms: string[]): { module: string; permissions: { key: string; label: string }[] }[] {
  const groups: Record<string, { key: string; label: string }[]> = {};
  for (const perm of perms) {
    const info = PERMISSION_LABELS[perm];
    const module = info?.module || 'permModule_other';
    const label = info?.label || perm;
    if (!groups[module]) groups[module] = [];
    groups[module].push({ key: perm, label });
  }
  return Object.entries(groups).map(([module, permissions]) => ({ module, permissions }));
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface RoleRecord {
  id: string;
  name: string;
  description?: string | null;
  permissions?: string | string[] | null;
  isSystem?: boolean;
  _count?: { userRoles?: number };
}

interface RolesResponse {
  data?: RoleRecord[];
  meta?: { total?: number };
  total?: number;
}

const defaultForm = {
  name: '',
  description: '',
  permissions: [] as string[],
};

export function RolesPage() {
  const { hasPermission, user } = useAuthStore();
  const { t } = useTranslation('settings');
  const canManage = hasPermission('users:*') || hasPermission('*');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleRecord | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; holders: number }>({
    open: false,
    id: '',
    name: '',
    holders: 0,
  });
  const [nameError, setNameError] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;
  const queryClient = useQueryClient();

  /* ----- queries ----- */

  const { data: roles, isLoading } = useQuery<RoleRecord[] | RolesResponse>({
    queryKey: ['roles', page],
    queryFn: async () => {
      const { data } = await api.get('/roles', { params: { page, limit } });
      return data;
    },
  });

  const rolesList: RoleRecord[] = Array.isArray(roles) ? roles : roles?.data ?? [];
  const rolesTotal: number = (Array.isArray(roles) ? undefined : roles?.meta?.total ?? roles?.total) ?? rolesList.length;

  // Derive available permissions from the current user's effective permissions
  // (which are already subscription-capped). Expand wildcards into sub-keys for granular control.
  const subscriptionPermissions = user?.permissions ?? [];
  const availablePermissions = Object.keys(PERMISSION_LABELS).filter((key) => {
    // Keep a permission key if the user's subscription covers the module
    return subscriptionPermissions.some((p: string) => {
      if (p === '*') return true;
      if (p === key) return true;
      if (p.endsWith(':*') && key.startsWith(p.replace(':*', ':'))) return true;
      return false;
    });
  });

  /* ----- form sync ----- */

  useEffect(() => {
    if (dialogOpen && editRole) {
      setForm({
        name: editRole.name ?? '',
        description: editRole.description ?? '',
        permissions: parsePermissions(editRole.permissions),
      });
      // expand all modules that have selected permissions
      const perms = parsePermissions(editRole.permissions);
      const modules: Record<string, boolean> = {};
      for (const p of perms) {
        const mod = PERMISSION_LABELS[p]?.module || 'Other';
        modules[mod] = true;
      }
      setExpandedModules(modules);
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
      setExpandedModules({});
    }
    setNameError('');
  }, [dialogOpen, editRole]);

  /* ----- mutations ----- */

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        description: form.description || undefined,
        permissions: form.permissions,
      };
      if (editRole) {
        const { data } = await api.patch(`/roles/${editRole.id}`, payload);
        return data;
      }
      const { data } = await api.post('/roles', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editRole ? t('roleUpdated') : t('roleCreated'));
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('saveFailed'));
      // POS-I335: name rules are shown under the Name field, other failures as a toast
      if (/role name|role named/i.test(message)) setNameError(message);
      else toast.error(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/roles/${id}`),
    onSuccess: () => {
      toast.success(t('roleDeleted'));
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('deleteFailed')));
    },
  });

  const applyTemplatesMutation = useMutation({
    mutationFn: () => api.post('/roles/meims-templates').then((r) => r.data),
    onSuccess: (data: { created: string[]; skipped: string[]; message: string }) => {
      // POS-I339: say which roles were made and which already existed
      const created = data.created ?? [];
      const skipped = data.skipped ?? [];
      toast.success(
        created.length > 0
          ? t('templatesCreated', { count: created.length, names: created.join(', ') })
          : t('templatesNoneCreated', { count: skipped.length, names: skipped.join(', ') }),
      );
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('templatesFailed')));
    },
  });

  /* ----- permission helpers ----- */

  const grouped = groupPermissions(availablePermissions ?? []);

  function togglePermission(key: string) {
    // For system admin role, prevent removing existing permissions
    const isAdminRole = editRole?.isSystem && editRole?.name === 'admin';
    const originalPerms = editRole ? parsePermissions(editRole.permissions) : [];

    setForm((prev) => {
      const has = prev.permissions.includes(key);

      // Block unchecking an existing permission on the admin role
      if (has && isAdminRole && originalPerms.includes(key)) return prev;

      let next = has ? prev.permissions.filter((p) => p !== key) : [...prev.permissions, key];

      // If toggling a wildcard on, add all individual perms in that module
      if (!has && key.endsWith(':*')) {
        const mod = PERMISSION_LABELS[key]?.module;
        if (mod) {
          const modulePerms = (availablePermissions ?? []).filter(
            (p) => p !== key && PERMISSION_LABELS[p]?.module === mod,
          );
          next = [...new Set([...next, ...modulePerms])];
        }
      }

      // If toggling a wildcard off, remove all individual perms in that module
      // (but keep ones that are locked on the admin role)
      if (has && key.endsWith(':*')) {
        const mod = PERMISSION_LABELS[key]?.module;
        if (mod) {
          const modulePerms = (availablePermissions ?? []).filter(
            (p) => PERMISSION_LABELS[p]?.module === mod,
          );
          next = next.filter((p) => {
            if (!modulePerms.includes(p)) return true;
            // Keep locked permissions for admin role
            if (isAdminRole && originalPerms.includes(p)) return true;
            return false;
          });
        }
      }

      return { ...prev, permissions: next };
    });
  }

  function toggleModuleExpand(module: string) {
    setExpandedModules((prev) => ({ ...prev, [module]: !prev[module] }));
  }

  /* ----- render ----- */

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('rolesAndPermissionsTitle')}
        subtitle={t('rolesSubtitle')}
        actions={canManage && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => applyTemplatesMutation.mutate()}
              disabled={applyTemplatesMutation.isPending}
              title={t('templatesTooltip', { count: 6 })}
            >
              <Sparkles className="me-2 h-4 w-4" />
              {t('templatesButton')}
            </Button>
            <Button onClick={() => { setEditRole(null); setDialogOpen(true); }}>
              <Plus className="me-2 h-4 w-4" />
              {t('createRole')}
            </Button>
          </div>
        )}
      />

      {/* Role Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="me-2 h-5 w-5 animate-spin" />
          {t('loadingRoles')}
        </div>
      ) : !rolesList.length ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Shield className="h-12 w-12 mb-3 opacity-30" />
          <p>{t('noRolesFound')}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rolesList.map((role) => {
            const perms = parsePermissions(role.permissions);
            const userCount = role._count?.userRoles ?? 0;
            const visiblePerms = perms.slice(0, 5);
            const extraCount = perms.length - visiblePerms.length;

            return (
              <Card
                key={role.id}
                className="min-w-0 animate-fade-in-up hover:shadow-lg transition-shadow duration-200"
              >
                <CardContent className="p-5">
                  {/* Top row */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <Shield className="h-5 w-5 shrink-0 text-primary-text" />
                      <h3 className="min-w-0 break-words text-lg font-semibold capitalize line-clamp-2" title={role.name}>{role.name}</h3>
                    </div>
                    {role.isSystem && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-text">
                        <Lock className="h-3 w-3" />
                        {t('system')}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {role.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {role.description}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {t('roleUserCount', { count: userCount })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="h-3.5 w-3.5" />
                      {t('rolePermCount', { count: perms.length })}
                    </span>
                  </div>

                  {/* Permission pills */}
                  {perms.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {visiblePerms.map((p) => (
                        <span
                          key={p}
                          className="inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary-text"
                        >
                          {getPermissionLabel(p, t)}
                        </span>
                      ))}
                      {extraCount > 0 && (
                        <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {t('moreCount', { count: extraCount })}
                        </span>
                      )}
                    </div>
                  )}

                  {canManage && (
                    <div className="flex justify-end gap-1 border-t pt-3">
                      <TableRowActions
                        actions={[
                          {
                            key: 'edit',
                            label: t('editRole'),
                            icon: Edit,
                            onSelect: () => {
                              setEditRole(role);
                              setDialogOpen(true);
                            },
                            primary: true,
                          },
                          {
                            key: 'delete',
                            label: t('deleteRole'),
                            icon: Trash2,
                            onSelect: () =>
                              setDeleteConfirm({ open: true, id: role.id, name: role.name, holders: userCount }),
                            permission: !role.isSystem,
                            variant: 'destructive',
                          },
                        ]}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {rolesTotal > limit && (
        <div className="border-t px-4 py-3">
          <PaginationBar page={page} pageSize={limit} total={rolesTotal} onPageChange={setPage} />
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header={editRole ? t('editRole') : t('createRole')}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              disabled={saveMutation.isPending || !form.name.trim()}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {editRole ? t('updateRole') : t('createRole')}
            </Button>
          </div>
        }
        style={{ width: '56rem' }}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        maximizable
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          {/* Subscription info banner */}
          <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2.5 text-sm text-primary-text">
            <Shield className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {t('permSubBanner')}
            </span>
          </div>

          {/* Name */}
          <div>
            <Label>{t('nameRequiredLabel')}</Label>
            <Input
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setNameError(''); }}
              placeholder={t('roleNamePlaceholder')}
              disabled={editRole?.isSystem}
              maxLength={100}
              aria-invalid={nameError ? true : undefined}
            />
            {nameError && <p className="mt-1 text-sm text-destructive" role="alert">{nameError}</p>}
          </div>

          {/* Description */}
          <div>
            <Label>{t('roleDescription')}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('optionalDescPlaceholder')}
              rows={3}
              maxLength={500}
              className="resize-none"
            />
          </div>

          {/* Permissions */}
          <div>
            <Label className="mb-2 block">{t('permissions')}</Label>
            {form.permissions.length === 0 && (
              <p className="mb-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-text" role="status">
                {t('roleNoPermsNotice')}
              </p>
            )}
            <div className="space-y-2 rounded-lg border p-3">
              {grouped.length === 0 && (
                <p className="text-sm text-muted-foreground py-2 text-center">
                  {t('noPermissionsAvailable')}
                </p>
              )}
              {grouped.map((group) => {
                const isExpanded = expandedModules[group.module] ?? false;
                const wildcardKey = group.permissions.find((p) => p.key.endsWith(':*'))?.key;
                const wildcardActive = wildcardKey ? form.permissions.includes(wildcardKey) : false;
                const individualPerms = group.permissions.filter((p) => !p.key.endsWith(':*'));

                return (
                  <div key={group.module} className="rounded-md border">
                    {/* Module header */}
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium hover:bg-accent/50 transition-colors rounded-md"
                      onClick={() => toggleModuleExpand(group.module)}
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span>{t(group.module)}</span>
                        <span className="text-xs text-muted-foreground">
                          ({group.permissions.filter((p) => form.permissions.includes(p.key)).length}/{group.permissions.length})
                        </span>
                      </div>
                    </button>

                    {/* Module permissions */}
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-1.5">
                        {/* Wildcard */}
                        {wildcardKey && (() => {
                          const isLocked = editRole?.isSystem && editRole?.name === 'admin'
                            && parsePermissions(editRole.permissions).includes(wildcardKey);
                          return (
                            <Label className={`flex items-center gap-2 rounded px-2 py-1 transition-colors font-normal leading-normal text-foreground ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-accent/30'}`}>
                              <Checkbox
                                checked={wildcardActive}
                                disabled={isLocked}
                                onChange={() => togglePermission(wildcardKey)}
                              />
                              <span className="text-sm font-medium text-primary-text">
                                {getPermissionLabel(wildcardKey, t)}
                              </span>
                              {isLocked && (
                                <Lock className="h-3 w-3 text-warning-text ms-auto" />
                              )}
                            </Label>
                          );
                        })()}

                        {/* Individual */}
                        {individualPerms.map((perm) => {
                          const isLocked = editRole?.isSystem && editRole?.name === 'admin'
                            && parsePermissions(editRole.permissions).includes(perm.key);
                          return (
                            <Label
                              key={perm.key}
                              className={`flex items-center gap-2 rounded px-2 py-1 transition-colors font-normal leading-normal text-foreground ${isLocked || wildcardActive ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-accent/30'}`}
                            >
                              <Checkbox
                                checked={form.permissions.includes(perm.key)}
                                disabled={wildcardActive || isLocked}
                                onChange={() => togglePermission(perm.key)}
                              />
                              <span className={`text-sm ${wildcardActive || isLocked ? 'text-muted-foreground' : ''}`}>
                                {t(perm.label)}
                              </span>
                              {isLocked && (
                                <Lock className="h-3 w-3 text-warning-text ms-auto" />
                              )}
                            </Label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('deleteRole')}
        message={deleteConfirm.holders > 0
          ? t('roleDeleteInUse', { name: deleteConfirm.name, count: deleteConfirm.holders })
          : t('deleteRoleConfirm', { name: deleteConfirm.name })}
        confirmLabel={deleteConfirm.holders > 0 ? t('roleDeleteInUseOk') : t('delete')}
        variant={deleteConfirm.holders > 0 ? 'warning' : 'destructive'}
        loading={deleteMutation.isPending}
        onConfirm={() => {
          // POS-I336: a role people still hold is never deleted; the button only closes the notice
          if (deleteConfirm.holders > 0) {
            setDeleteConfirm({ open: false, id: '', name: '', holders: 0 });
            return;
          }
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '', holders: 0 }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '', holders: 0 })}
      />
    </div>
  );
}
